import { useCallback, useEffect, useRef } from "react"
import { StyleSheet, View } from "react-native"

import { useFocusEffect } from "expo-router"

import { AvatarOverlay } from "src/components/SidekickView/AvatarOverlay"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useRiveContext } from "src/context/RiveContext"
import { useVisemes } from "src/context/VisemeContext"
import { useUnauthCmSubscriptionBenefits } from "src/queries/useUnauthCmSubscriptionBenefits"
import { useUnauthIntroMessage } from "src/queries/useUnauthIntroMessage"
import { storage } from "src/utils/storage/mmkv"

/** MMKV key: whether the channel intro has been played for a given channel. */
function channelWelcomeShownKey(channelId: string) {
  return `unauth.channelWelcomeShown.${channelId}`
}

/** MMKV key: whether the CM subscription-benefits message has been played for a given task. */
function cmBenefitsShownKey(channelId: string, taskId: string) {
  return `unauth.cmBenefitsShown.${channelId}.${taskId}`
}

/** Delay after the welcome message finishes before transitioning from large to the resting size. */
const AUTO_SIZE_TRANSITION_DELAY_MS = 3000

/**
 * How long to wait for isRiveReady to go false after applying a new avatar ID before
 * assuming the avatar didn't change the Rive file (same ID) and queuing speech anyway.
 */
const SAME_AVATAR_FALLBACK_MS = 600

interface UnauthAvatarOverlayProps {
  channelId: string
  /**
   * When provided the overlay fetches and plays the CM subscription-benefits
   * message (GET /cm_subscription_benefits/<channelId>/<taskId>) instead of the
   * per-channel intro message. First-visit gating is keyed per task.
   */
  taskId?: string
  /**
   * When true, cancels any pending post-intro auto-size transition so the media
   * player's own avatar-mode management (tab while playing) takes precedence.
   */
  isMediaPlaying?: boolean
}

/**
 * Renders the Rive avatar overlay on the unauthenticated content library screen.
 *
 * Sequencing for first-visit:
 *  1. When introMessage arrives → apply avatar_id immediately (correct avatar from the start).
 *  2. Watch isRiveReady go false (old Rive unloading) then true (new Rive loaded).
 *  3. Once the new avatar's Rive is confirmed ready, queue the intro speech with a
 *     setTimeout(0) to ensure useSpeechMarkPlayerWeb's riveWebRef.current is populated.
 *  4. After speech finishes, auto-transition to resting size after AUTO_SIZE_TRANSITION_DELAY_MS.
 *
 * Fallback: if the channel's avatar_id maps to the same Rive file that's already loaded
 * (e.g. channel avatar is also ID 0), isRiveReady never goes false. In that case we
 * queue speech after SAME_AVATAR_FALLBACK_MS using the already-loaded Rive instance.
 *
 * Subsequent visits start in "tab" mode with no auto-play.
 */
export function UnauthAvatarOverlay({
  channelId,
  taskId,
  isMediaPlaying,
}: UnauthAvatarOverlayProps) {
  const { mode, setMode } = useAvatarMode()
  const { setUnauthAvatarId, setUnauthAvatarName, isRiveReady } = useRiveContext()
  const { queueSpeech, isSpeaking } = useVisemes()

  // Per-task mode: gate on whether the CM benefits message has been played.
  // Per-channel mode: gate on whether the channel intro has been played.
  const storageKey = taskId
    ? cmBenefitsShownKey(channelId, taskId)
    : channelWelcomeShownKey(channelId)

  // Capture isFirstVisit once at mount so re-renders (from storage.set) don't flip it.
  const isFirstVisitRef = useRef(storage.getBoolean(storageKey) !== true)
  const isFirstVisit = isFirstVisitRef.current

  // Only fetch the relevant query; pass null to disable the other.
  const { data: introData, isError: isIntroError } = useUnauthIntroMessage(
    taskId ? null : channelId,
  )
  const { data: cmBenefitsData, isError: isCmBenefitsError } = useUnauthCmSubscriptionBenefits(
    taskId ? channelId : null,
    taskId ?? null,
  )
  const introMessage = taskId ? cmBenefitsData : introData
  const isIntroMessageError = taskId ? isCmBenefitsError : isIntroError

  // Speech id distinguishes channel intro from per-task messages in the queue.
  const speechId = taskId ? `cm-benefits-${channelId}-${taskId}` : `intro-${channelId}`

  // ── sequencing refs ──────────────────────────────────────────────────────────
  /** True after setUnauthAvatarId was called with the channel avatar_id. */
  const avatarAppliedRef = useRef(false)
  /**
   * True after isRiveReady went false following the avatar ID being applied.
   * This confirms the old Rive instance was torn down and the new one is loading.
   */
  const riveWentFalseRef = useRef(false)
  const hasQueuedSpeechRef = useRef(false)

  // ── auto-size-transition refs ─────────────────────────────────────────────────
  const hasSpeechStartedRef = useRef(false)
  const autoSizeTransitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sameAvatarFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ─── set initial avatar mode ─────────────────────────────────────────────────
  useEffect(() => {
    // Channel intro expands to large on first visit; CM-benefits messages use
    // minimized so the avatar is visible but doesn't obscure the media content.
    if (taskId) {
      setMode("minimized", false)
    } else {
      setMode(isFirstVisit ? "large" : "tab", false)
    }
  }, [isFirstVisit, taskId, setMode])

  // ─── fall back to tab mode when intro message fails (e.g. 500) ───────────────
  // On a first visit, the avatar starts large while waiting for the intro message.
  // If the fetch fails the speech will never play, so collapse to tab immediately.
  useEffect(() => {
    if (!isIntroMessageError || !isFirstVisit) return
    setMode("tab", false)
  }, [isIntroMessageError, isFirstVisit, setMode])

  // ─── non-first visit: apply avatar immediately, no speech ────────────────────
  useEffect(() => {
    if (isFirstVisit || !introMessage) return
    setUnauthAvatarId(introMessage.avatar_id)
    setUnauthAvatarName(introMessage.psk_name)
  }, [isFirstVisit, introMessage, setUnauthAvatarId, setUnauthAvatarName])

  // ─── re-apply avatar ID when this screen regains focus ───────────────────────
  // When a child screen (e.g. media-viewer) pops, its UnauthAvatarOverlay unmount
  // cleanup calls setUnauthAvatarId(null). This overlay stays mounted during that
  // navigation, so its mount effects don't re-run. Reapplying here restores the
  // correct avatar whenever this screen comes back to the foreground.
  useFocusEffect(
    useCallback(() => {
      if (!introMessage) return
      setUnauthAvatarId(introMessage.avatar_id)
      setUnauthAvatarName(introMessage.psk_name)
    }, [introMessage, setUnauthAvatarId, setUnauthAvatarName]),
  )

  // ─── first-visit step 1: apply avatar ID as soon as introMessage arrives ─────
  // Setting the avatar ID before speech shows the right avatar from the start.
  // It causes AvatarRive to reload (isRiveReady: true → false → true), which we
  // observe in step 2/3 to know when it's safe to call queueSpeech.
  useEffect(() => {
    if (!introMessage || !isFirstVisit || avatarAppliedRef.current) return
    avatarAppliedRef.current = true
    riveWentFalseRef.current = false
    setUnauthAvatarId(introMessage.avatar_id)
    setUnauthAvatarName(introMessage.psk_name)
  }, [introMessage, isFirstVisit, setUnauthAvatarId, setUnauthAvatarName])

  // ─── first-visit step 2: detect isRiveReady going false post-avatar-apply ────
  useEffect(() => {
    if (!avatarAppliedRef.current || !isFirstVisit) return
    if (!isRiveReady) {
      riveWentFalseRef.current = true
    }
  }, [isRiveReady, isFirstVisit])

  // ─── shared helper: queue the intro speech ───────────────────────────────────
  const doQueueSpeech = useCallback(() => {
    if (!introMessage || hasQueuedSpeechRef.current) return
    hasQueuedSpeechRef.current = true
    storage.set(storageKey, true)
    // setTimeout(0) yields to the JS event loop so that useSpeechMarkPlayerWeb's
    // effect (which populates riveWebRef.current) has settled before animateSpeech runs.
    setTimeout(() => {
      queueSpeech({
        id: speechId,
        audioUrl: introMessage.audio_url,
        speechMarks: introMessage.speech_marks,
      })
    }, 0)
  }, [introMessage, speechId, storageKey, queueSpeech])

  // ─── first-visit step 3: queue speech once new Rive is confirmed ready ────────
  useEffect(() => {
    if (!introMessage || !isFirstVisit || hasQueuedSpeechRef.current) return
    if (!isRiveReady || !avatarAppliedRef.current) return

    if (riveWentFalseRef.current) {
      // Rive completed a full reload cycle after the avatar ID was applied — the
      // new avatar is loaded and riveWebRef.current is valid.
      doQueueSpeech()
      return
    }

    // Avatar was applied but isRiveReady hasn't gone false yet. Two cases:
    //   a) Rive is still in the process of tearing down (will go false shortly) — wait.
    //   b) The channel avatar_id maps to the same Rive file already loaded (no reload).
    // Schedule a fallback to handle case (b).
    if (sameAvatarFallbackRef.current) return
    sameAvatarFallbackRef.current = setTimeout(() => {
      sameAvatarFallbackRef.current = null
      if (!hasQueuedSpeechRef.current && !riveWentFalseRef.current) {
        doQueueSpeech()
      }
    }, SAME_AVATAR_FALLBACK_MS)

    return () => {
      if (sameAvatarFallbackRef.current) {
        clearTimeout(sameAvatarFallbackRef.current)
        sameAvatarFallbackRef.current = null
      }
    }
  }, [isRiveReady, introMessage, isFirstVisit, doQueueSpeech])

  // ─── cancel auto-size transition when media takes over playback ───────────────
  // The auto-size transition timer starts when the intro finishes. If the media
  // player autoplays during that window, cancel the transition so the media
  // player's own avatar-mode management takes precedence.
  useEffect(() => {
    if (!isMediaPlaying) return
    if (autoSizeTransitionTimerRef.current !== null) {
      clearTimeout(autoSizeTransitionTimerRef.current)
      autoSizeTransitionTimerRef.current = null
    }
  }, [isMediaPlaying])

  // ─── watch isSpeaking for auto-size transition ────────────────────────────────
  useEffect(() => {
    if (!isFirstVisit || !hasQueuedSpeechRef.current) return

    if (isSpeaking) {
      hasSpeechStartedRef.current = true
      if (autoSizeTransitionTimerRef.current !== null) {
        clearTimeout(autoSizeTransitionTimerRef.current)
        autoSizeTransitionTimerRef.current = null
      }
    } else if (hasSpeechStartedRef.current) {
      hasSpeechStartedRef.current = false
      autoSizeTransitionTimerRef.current = setTimeout(() => {
        autoSizeTransitionTimerRef.current = null
        setMode("tab", false)
      }, AUTO_SIZE_TRANSITION_DELAY_MS)
    }

    return () => {
      if (autoSizeTransitionTimerRef.current !== null) {
        clearTimeout(autoSizeTransitionTimerRef.current)
        autoSizeTransitionTimerRef.current = null
      }
    }
  }, [isSpeaking, isFirstVisit, setMode])

  // ─── clear avatar override on unmount ────────────────────────────────────────
  const clearAvatarOverride = useCallback(() => {
    setUnauthAvatarId(null)
    setUnauthAvatarName(null)
  }, [setUnauthAvatarId, setUnauthAvatarName])
  useEffect(() => {
    return clearAvatarOverride
  }, [clearAvatarOverride])

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
      <AvatarOverlay
        riveOwner="primary"
        hideConversationControls
        showCaptions
        useFlexLayout={mode === "large"}
      />
    </View>
  )
}
